import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Calendar1,
  Loader2,
  Video,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type MeetingView, type TeamScope } from '../../lib/client.ts';
import { useSession } from '../../lib/session.tsx';
import { useSettings } from '../../lib/settings.ts';
import { cn } from '../../lib/utils.ts';
import { openMeetingCompanion } from '../meeting-companion/meeting.ts';
import { BlockCard } from '../records/block-card.tsx';
import { Button } from '../ui/button.tsx';
import { EmptyState } from '../ui/empty-state.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { RAIL } from '../ui/urgency-rail.ts';
import {
  addDays,
  ago,
  firstOfMonth,
  monthGrid,
  sameDay,
  startOfDay,
  timeOfDay,
  useNow,
} from './time.ts';

/**
 * The Calendar: my Calendly meetings, in my time zone, from the snapshot the worker refreshes every
 * 5 minutes (`GET /meetings/mine`). Two views of the same meetings:
 *
 *  - **Day** (the default; the toggle's choice is kept per user, `settings.calendarView`): one
 *    day's time grid, today first, with the days around it a click away. The whole day scrolls; it
 *    opens at 08:00, or at the first meeting's hour when one starts earlier, and its 30-minute
 *    slots are sized so 08:00–20:00 fills the room the card gives it, between `SLOT_MIN_PX` and
 *    `SLOT_MAX_PX`. 16 back-to-back 30-minute meetings fit in that view.
 *  - **Month**, as Calendly draws it: a dot under each day per meeting (up to 3), and under it the
 *    selected day's meetings (today by default: those still to come). A day selects it, in Day too.
 *
 * One read per month grid (42 days, the API's window), whichever view is open. A meeting opens the
 * Meeting Companion (notes and tasks beside the call), its Join opens the call.
 *
 * With `team` (Team Tasks) it shows the chosen people's meetings (`GET /meetings/team`), each with
 * its host's name; only the caller's own open the companion or carry Join (the API sends someone
 * else's without a link).
 */
const SLOT_MIN_PX = 18;
const SLOT_MAX_PX = 28;
/** Full screen, or before the room is measured. */
const SLOT_PX = 20;
/** The hours Day's view is sized to show; it opens at the first, or earlier for an earlier meeting. */
const DAY_FROM = 8;
const DAY_TO = 20;
const MONTH = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });
const WEEKDAY = new Intl.DateTimeFormat(undefined, { weekday: 'narrow' });
const LONG_DAY = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
});

/** A meeting as either endpoint serves it; Team Tasks' carry their host. */
type CalendarMeeting = MeetingView & { host?: { id: string; name: string } };
type View = 'day' | 'month';

export function CalendarCard({ team, fill = false }: { team?: TeamScope; fill?: boolean }) {
  const me = useSession().me.user.id;
  const [settings, updateSettings] = useSettings(me);
  // Re-read every minute, so a tab left open past midnight moves on to the new day.
  const today = startOfDay(new Date(useNow()));
  // Opens on the view last chosen with the toggle (Day by default).
  const [view, setView] = useState<View>(settings.calendarView);
  const chooseView = (v: View) => {
    setView(v);
    updateSettings({ calendarView: v });
  };
  const [day, setDay] = useState(today);
  const [month, setMonth] = useState(firstOfMonth(today));
  // The month shown always holds the day Day shows, so one read serves both views.
  const pickDay = (d: Date) => {
    setDay(d);
    setMonth(firstOfMonth(d));
  };
  // At midnight, a card left on today follows it; one left on another day stays there.
  const [shownToday, setShownToday] = useState(today);
  if (!sameDay(shownToday, today)) {
    setShownToday(today);
    if (sameDay(day, shownToday)) pickDay(today);
  }
  const read = (first: Date) => {
    const grid = monthGrid(first);
    const from = grid[0]!.toISOString();
    const to = addDays(grid[0]!, grid.length).toISOString();
    return {
      queryKey: team ? ['meetings', 'team', from, team] : ['meetings', 'mine', from],
      queryFn: () => (team ? api.meetings.team(from, to, team) : api.meetings.mine(from, to)),
      refetchInterval: 60_000,
    };
  };
  // The month moves only when asked; the old one stays up until the new one is read.
  const meetings = useQuery({ ...read(month), placeholderData: keepPreviousData });
  const data = meetings.data;
  const isMine = (m: CalendarMeeting) => !m.host || m.host.id === me;
  return (
    <BlockCard
      title="Calendar"
      fill={fill}
      filter={
        <div
          className="bg-muted flex gap-0.5 rounded-md p-0.5"
          role="group"
          aria-label="Calendar view"
        >
          {(
            [
              ['day', Calendar1, 'Day'],
              ['month', CalendarDays, 'Month'],
            ] as const
          ).map(([v, Icon, label]) => (
            <Button
              key={v}
              variant="ghost"
              size="sm"
              className={cn(
                'h-6 px-2 text-xs',
                view === v
                  ? 'bg-card text-foreground hover:bg-card shadow-xs'
                  : 'text-muted-foreground hover:bg-transparent hover:text-foreground',
              )}
              aria-pressed={view === v}
              onClick={() => chooseView(v)}
            >
              <Icon aria-hidden /> {label}
            </Button>
          ))}
        </div>
      }
    >
      {(full) =>
        meetings.isPending ? (
          <p className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin" aria-hidden /> Loading…
          </p>
        ) : !data?.refreshedAt ? (
          <div className="flex flex-col items-start gap-2" data-testid="calendar-unavailable">
            <p className="text-danger text-sm" role="alert">
              {meetings.error
                ? `Meetings could not be loaded: ${describeError(meetings.error)}`
                : 'Meetings have not been read from Calendly yet.'}
            </p>
            <Button variant="outline" size="sm" onClick={() => void meetings.refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          // Fills the card: an empty day's state grows, and "Last updated" sits at the foot.
          // pt-1: room for the month picker's focus ring, which the card's scroll area would clip.
          <div className="flex min-h-full flex-auto flex-col gap-2 pt-1">
            {view === 'month' ? (
              <>
                {/* Full screen: the month at 80% of the width, centred; the day's list below it fills the rest. */}
                <div className={full ? 'mx-auto w-4/5' : undefined}>
                  <MonthView
                    month={month}
                    today={today}
                    selected={day}
                    items={data.items}
                    span={data.span}
                    onMonth={(next) => pickDay(sameDay(next, firstOfMonth(today)) ? today : next)}
                    onDay={pickDay}
                  />
                </div>
                {/* Only the day's list scrolls; the month stays in place above it. */}
                <div className="relative min-h-48 flex-1 lg:min-h-24">
                  <div
                    className="absolute inset-0 overflow-y-auto"
                    data-testid="calendar-day-scroll"
                  >
                    <DayList day={day} today={today} items={data.items} isMine={isMine} />
                  </div>
                </div>
              </>
            ) : (
              <DayView
                day={day}
                today={today}
                items={data.items}
                fit={fill && !full}
                isMine={isMine}
                onDay={pickDay}
              />
            )}
            <p
              className={`mt-auto flex items-center gap-1 text-xs ${data.stale ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}
              data-testid="calendar-updated"
            >
              {data.stale && <AlertTriangle className="size-3.5" aria-hidden />}
              {data.stale ? 'May be out of date: last updated ' : 'Last updated '}
              {ago(data.refreshedAt)} · Calendly meetings only
            </p>
          </div>
        )
      }
    </BlockCard>
  );
}

/** The month as Calendly draws it: a dot under a day per meeting (up to 3). */
function MonthView({
  month,
  today,
  selected,
  items,
  span,
  onMonth,
  onDay,
}: {
  month: Date;
  today: Date;
  selected: Date;
  items: CalendarMeeting[];
  /** The first and last meeting the caller may see: the months the picker offers. */
  span: { first: string; last: string } | null;
  /** The 1st of the month to show. */
  onMonth: (first: Date) => void;
  onDay: (d: Date) => void;
}) {
  const days = monthGrid(month);
  const step = (n: number) => onMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  // The months with data, plus this one and the one shown, so the picker always holds both.
  const ends = [today, month, ...(span ? [new Date(span.first), new Date(span.last)] : [])].map(
    (d) => d.getFullYear() * 12 + d.getMonth(),
  );
  const first = Math.min(...ends);
  const months = Array.from(
    { length: Math.max(...ends) - first + 1 },
    (_, i) => new Date(Math.floor((first + i) / 12), (first + i) % 12, 1),
  );
  const keyOfMonth = (d: Date) => `${d.getFullYear()}-${d.getMonth()}`;
  const perDay = new Map<string, number>();
  for (const m of items) {
    const key = startOfDay(new Date(m.startAt)).toDateString();
    perDay.set(key, (perDay.get(key) ?? 0) + 1);
  }
  return (
    <div data-testid="calendar-month">
      <NavRow
        before={
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label="Previous month"
            onClick={() => step(-1)}
          >
            <ChevronLeft />
          </Button>
        }
        after={
          <>
            {!sameDay(selected, today) && <TodayButton onClick={() => onDay(today)} />}
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label="Next month"
              onClick={() => step(1)}
            >
              <ChevronRight />
            </Button>
          </>
        }
      >
        <Select
          value={keyOfMonth(month)}
          onValueChange={(v) => {
            const [y, m] = v.split('-').map(Number);
            onMonth(new Date(y!, m!, 1));
          }}
        >
          <SelectTrigger aria-label="Month" className="h-7 w-auto gap-1 px-2 font-semibold">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {months.map((d) => (
              <SelectItem key={keyOfMonth(d)} value={keyOfMonth(d)}>
                {MONTH.format(d)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </NavRow>
      <div className="text-muted-foreground grid grid-cols-7 text-center text-[11px]" aria-hidden>
        {days.slice(0, 7).map((d) => (
          <span key={d.toDateString()}>{WEEKDAY.format(d)}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 text-center">
        {days.map((d) => {
          const n = perDay.get(d.toDateString()) ?? 0;
          const outside = d.getMonth() !== month.getMonth();
          const isToday = sameDay(d, today);
          const isSelected = sameDay(d, selected);
          return (
            <button
              key={d.toDateString()}
              type="button"
              onClick={() => onDay(d)}
              aria-pressed={isSelected}
              aria-label={`${LONG_DAY.format(d)}, ${n} meeting${n === 1 ? '' : 's'}`}
              data-testid="calendar-day"
              data-meetings={n}
              className="hover:bg-muted/70 flex h-10 flex-col items-center justify-center gap-0.5 rounded-md"
            >
              <span
                className={cn(
                  'flex size-7 items-center justify-center rounded-full text-sm tabular-nums',
                  isToday && 'bg-primary text-primary-foreground font-semibold',
                  isSelected && !isToday && 'ring-primary text-primary font-semibold ring-1',
                  outside && !isToday && 'text-muted-foreground/50',
                  n > 0 && !isToday && !outside && 'font-semibold',
                )}
              >
                {d.getDate()}
              </span>
              <span className="flex h-1.5 gap-0.5" aria-hidden>
                {Array.from({ length: Math.min(n, 3) }, (_, i) => (
                  <span key={i} className="bg-primary size-1.5 rounded-full" />
                ))}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Under the month: the selected day's meetings. Today's are those still to come, the next one on
 * the brand rail; another day's are all of them.
 */
function DayList({
  day,
  today,
  items,
  isMine,
}: {
  day: Date;
  today: Date;
  items: CalendarMeeting[];
  isMine: (m: CalendarMeeting) => boolean;
}) {
  const isToday = sameDay(day, today);
  const ofDay = items
    .filter((m) => sameDay(startOfDay(new Date(m.startAt)), day))
    .sort((a, b) => a.startAt.localeCompare(b.startAt));
  const now = Date.now();
  const coming = ofDay.filter((m) => Date.parse(m.endAt) > now);
  const past = ofDay.filter((m) => Date.parse(m.endAt) <= now);
  const nextKey = isToday && coming[0] ? keyOf(coming[0]) : null;
  const empty =
    ofDay.length > 0
      ? `Nothing more for ${isToday ? 'today' : 'this day'}.`
      : isToday
        ? 'Nothing scheduled for today.'
        : 'No meetings on this day.';
  return (
    <section
      aria-label={isToday ? 'Next up' : LONG_DAY.format(day)}
      className="flex flex-col gap-2"
      data-testid="calendar-day-list"
    >
      {/* What is still to come, boxed; what ended sits under it, outside the box. */}
      <div className="bg-primary/5 rounded-lg px-3 py-2">
        <h4 className="text-primary mb-1 text-xs font-semibold">
          {isToday ? 'Next up' : LONG_DAY.format(day)}
        </h4>
        {coming.length > 0 ? (
          <ul className="flex flex-col gap-1" data-testid="calendar-day-list-coming">
            {coming.map((m) => (
              <DayListRow key={keyOf(m)} meeting={m} isMine={isMine} next={keyOf(m) === nextKey} />
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-sm" data-testid="calendar-day-list-empty">
            {empty}
          </p>
        )}
      </div>
      {past.length > 0 && (
        <div className="px-3" data-testid="calendar-day-list-past">
          <h4 className="text-muted-foreground mb-1 text-xs font-semibold">Past</h4>
          <ul className="text-muted-foreground flex flex-col gap-1">
            {past.map((m) => (
              <DayListRow key={keyOf(m)} meeting={m} isMine={isMine} next={false} ended />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/** A meeting under the month: mine open the Meeting Companion (notes after the call too). */
function DayListRow({
  meeting: m,
  isMine,
  next,
  ended = false,
}: {
  meeting: CalendarMeeting;
  isMine: (m: CalendarMeeting) => boolean;
  next: boolean;
  /** Over: no Join. */
  ended?: boolean;
}) {
  return (
    <li
      className={cn('flex items-center gap-2 py-0.5 pl-2 text-sm', next && RAIL.brand)}
      data-next={next || undefined}
    >
      {isMine(m) ? (
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-1 text-left hover:underline"
          onClick={() => openCompanion(m)}
          aria-label={`Notes for ${m.title} at ${timeOfDay(m.startAt)}`}
        >
          <MeetingLabel meeting={m} />
        </button>
      ) : (
        <span className="flex min-w-0 flex-1 items-center gap-1">
          <MeetingLabel meeting={m} />
        </span>
      )}
      {!ended && <JoinLink meeting={m} />}
    </li>
  );
}

/** Back to today, beside the "next" arrow; only shown away from today. */
function TodayButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={onClick}>
      Today
    </Button>
  );
}

/** ◀ | title | (Today) ▶: equal side columns keep the title centred whatever is beside it. */
function NavRow({
  before,
  after,
  children,
}: {
  before: ReactNode;
  after: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="mb-1 grid grid-cols-[1fr_auto_1fr] items-center gap-1">
      <div className="flex justify-start">{before}</div>
      {children}
      <div className="flex items-center justify-end gap-1">{after}</div>
    </div>
  );
}

const keyOf = (m: CalendarMeeting) => `${m.id}:${m.host?.id ?? ''}`;

/** One day's time grid, with the days around it. */
function DayView({
  day,
  today,
  items,
  fit,
  isMine,
  onDay,
}: {
  day: Date;
  today: Date;
  items: CalendarMeeting[];
  /** Size the grid to the card's room (a `fill` card), else its natural height. */
  fit: boolean;
  isMine: (m: CalendarMeeting) => boolean;
  onDay: (d: Date) => void;
}) {
  const start = day.getTime();
  const end = addDays(day, 1).getTime();
  const ofDay = items.filter((m) => Date.parse(m.startAt) < end && Date.parse(m.endAt) > start);
  return (
    <div className="flex flex-1 flex-col gap-2">
      <NavRow
        before={
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label="Previous day"
            onClick={() => onDay(addDays(day, -1))}
          >
            <ChevronLeft />
          </Button>
        }
        after={
          <>
            {!sameDay(day, today) && <TodayButton onClick={() => onDay(today)} />}
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label="Next day"
              onClick={() => onDay(addDays(day, 1))}
            >
              <ChevronRight />
            </Button>
          </>
        }
      >
        <span className="text-sm font-semibold" data-testid="calendar-day-label">
          {LONG_DAY.format(day)}
        </span>
      </NavRow>
      {ofDay.length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          title="No meetings on this day"
          data-testid="calendar-day-empty"
          className="flex-1 py-8"
        />
      ) : (
        <CalendarView items={ofDay} from={day.toISOString()} fit={fit} isMine={isMine} />
      )}
    </div>
  );
}

const openCompanion = (m: MeetingView) =>
  openMeetingCompanion({
    id: m.id,
    title: m.title,
    start: m.startAt,
    end: m.endAt,
    joinUrl: m.joinUrl,
    attendees: m.attendees,
  });

function MeetingLabel({ meeting }: { meeting: CalendarMeeting }) {
  return (
    <>
      <span className="text-muted-foreground shrink-0 tabular-nums">
        {timeOfDay(meeting.startAt)}
      </span>
      {meeting.host && <span className="shrink-0 font-semibold">{meeting.host.name} ·</span>}
      <span className="min-w-0 flex-1 truncate font-medium">{meeting.title}</span>
    </>
  );
}

function JoinLink({ meeting }: { meeting: MeetingView }) {
  if (!meeting.joinUrl) return null;
  return (
    <a
      href={meeting.joinUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary inline-flex shrink-0 items-center gap-1 text-xs font-medium hover:underline"
      aria-label={`Join ${meeting.title} on ${meeting.platform}`}
    >
      <Video className="size-3.5" aria-hidden /> Join
    </a>
  );
}

interface Placed {
  meeting: CalendarMeeting;
  start: number;
  end: number;
  col: number;
  cols: number;
}

/** Overlapping meetings share the width side by side, as calendars draw them. */
function placeMeetings(items: CalendarMeeting[]): Placed[] {
  const sorted = [...items].sort((a, b) => a.startAt.localeCompare(b.startAt));
  const placed: Placed[] = [];
  let cluster: Placed[] = [];
  let clusterEnd = -Infinity;
  const flush = () => {
    const cols = Math.max(...cluster.map((p) => p.col)) + 1;
    for (const p of cluster) placed.push({ ...p, cols });
    cluster = [];
  };
  for (const meeting of sorted) {
    const start = Date.parse(meeting.startAt);
    // As drawn: a box is at least one slot tall, so a short meeting still takes its 30 minutes.
    const end = Math.max(Date.parse(meeting.endAt), start + 30 * 60_000);
    if (cluster.length > 0 && start >= clusterEnd) flush();
    const busy = new Set(cluster.filter((p) => p.end > start).map((p) => p.col));
    let col = 0;
    while (busy.has(col)) col++;
    cluster.push({ meeting, start, end, col, cols: 1 });
    clusterEnd = Math.max(clusterEnd, end);
  }
  if (cluster.length > 0) flush();
  return placed;
}

function CalendarView({
  items,
  from,
  fit,
  isMine,
}: {
  items: CalendarMeeting[];
  from: string;
  fit: boolean;
  isMine: (m: CalendarMeeting) => boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const [room, setRoom] = useState(0);
  useEffect(() => {
    const el = box.current;
    if (!fit || !el) return;
    const observer = new ResizeObserver(([entry]) => setRoom(entry?.contentRect.height ?? 0));
    observer.observe(el);
    return () => observer.disconnect();
  }, [fit]);
  const dayStart = Date.parse(from);
  const hourOf = (t: number) => (t - dayStart) / 3_600_000;
  const placed = placeMeetings(items);
  const nowAt = hourOf(useNow());
  const now = nowAt >= 0 && nowAt < 24 ? nowAt : null;
  // The whole day scrolls; it opens at 08:00, or at the first meeting's hour when that is earlier.
  const openAt = Math.max(
    0,
    Math.min(DAY_FROM, Math.floor(Math.min(...placed.map((p) => hourOf(p.start))))),
  );
  // Slots sized so 08:00–20:00 fills the room (less the grid's top margin for the first label).
  const shown = (DAY_TO - DAY_FROM) * 2;
  const slot =
    fit && room > 0
      ? Math.min(SLOT_MAX_PX, Math.max(SLOT_MIN_PX, Math.floor((room - 8) / shown)))
      : SLOT_PX;
  const px = (hours: number) => hours * 2 * slot;
  const height = px(24);
  const hours = Array.from({ length: 24 }, (_, i) => i);
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = px(openAt);
    // Only when the day or its first meeting changes, not on every "now" tick or resize.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, openAt]);
  // A resize keeps the hour the user scrolled to.
  const lastSlot = useRef(slot);
  useEffect(() => {
    if (scroller.current && lastSlot.current !== slot)
      scroller.current.scrollTop *= slot / lastSlot.current;
    lastSlot.current = slot;
  }, [slot]);
  return (
    // Fit: the box takes what is left of the card (at least 30rem when the cards stack); full
    // screen, it shows the same 12 hours. Either way the rest of the day is a scroll away.
    <div ref={box} className={fit ? 'relative min-h-[30rem] flex-1 lg:min-h-0' : undefined}>
      <div
        ref={scroller}
        className={fit ? 'absolute inset-0 overflow-y-auto' : 'overflow-y-auto'}
        style={fit ? undefined : { maxHeight: px(DAY_TO - DAY_FROM) + 8 }}
        data-testid="calendar-grid"
      >
        <div className="relative ml-14 mt-2" style={{ height }}>
          {hours.map((h) => (
            <div
              key={h}
              className="border-border absolute right-0 left-0 border-t"
              style={{ top: px(h) }}
            >
              <span className="text-muted-foreground absolute -top-2 -left-14 w-12 text-right text-[10px] whitespace-nowrap tabular-nums">
                {timeOfDay(new Date(dayStart + h * 3_600_000).toISOString())}
              </span>
            </div>
          ))}
          {now !== null && (
            <div
              className="pointer-events-none absolute right-0 left-0 z-10 border-t-2 border-red-500"
              style={{ top: px(now) }}
              data-testid="calendar-now"
              title={`Now, ${timeOfDay(new Date(dayStart + now * 3_600_000).toISOString())}`}
            >
              <span className="absolute -top-[5px] -left-1.5 size-2 rounded-full bg-red-500" />
            </div>
          )}
          {placed.map((p) => {
            const top = px(Math.max(0, hourOf(p.start)));
            const bottom = px(Math.min(24, hourOf(p.end)));
            return (
              <div
                key={keyOf(p.meeting)}
                className="bg-primary/10 border-primary/40 absolute flex items-start gap-1 overflow-hidden rounded-md border px-1.5 text-xs leading-[18px]"
                style={{
                  top: top + 1,
                  height: Math.max(slot - 2, bottom - top - 2),
                  left: `${(p.col / p.cols) * 100}%`,
                  width: `calc(${100 / p.cols}% - 2px)`,
                }}
                title={`${timeOfDay(p.meeting.startAt)}–${timeOfDay(p.meeting.endAt)} ${p.meeting.host ? `${p.meeting.host.name} · ` : ''}${p.meeting.title}`}
                data-testid="calendar-meeting"
              >
                {isMine(p.meeting) ? (
                  <>
                    {/* Opens the Meeting Companion; called in the click itself, as the window needs. */}
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-start gap-1 text-left hover:underline"
                      onClick={() => openCompanion(p.meeting)}
                      aria-label={`Notes for ${p.meeting.title} at ${timeOfDay(p.meeting.startAt)}`}
                    >
                      <MeetingLabel meeting={p.meeting} />
                    </button>
                    <JoinLink meeting={p.meeting} />
                  </>
                ) : (
                  <span className="flex min-w-0 flex-1 items-start gap-1">
                    <MeetingLabel meeting={p.meeting} />
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
